import type {
	StatementActivityAdjustmentKind,
	StatementActivityModel,
	StatementActivityPaymentKind,
} from '../models/interfaces/customer-statement.interface';

/**
 * PD-16379 — customer-facing labels for the lines in a statement's
 * "Payments & credits" section. Shared so the CRM screen, the PDF, and the
 * storefront describe the same line the same way (the screens pass them
 * through `t()`).
 */
export const STATEMENT_PAYMENT_KIND_LABELS: Record<
	StatementActivityPaymentKind,
	string
> = {
	PAYMENT: 'Payment',
	ACCOUNT_CREDIT_SPEND: 'Applied from account credit',
	REFUND: 'Refund',
	VOID: 'Reversal',
	ACCOUNT_CREDIT: 'Account credit',
};

/**
 * Labels for account-credit lines, by `customer_funds_ledger` type. Unknown
 * types fall back to the generic ACCOUNT_CREDIT label. A Map rather than an
 * object literal so a type string such as "constructor" cannot resolve to an
 * inherited property.
 */
const ACCOUNT_CREDIT_TYPE_LABELS = new Map<string, string>([
	['deposit', 'Deposited to account'],
	['credit_memo', 'Credit memo moved to account credit'],
	['refund', 'Refunded to account credit'],
	['spend', 'Applied from account credit'],
	['spend_revert', 'Account credit restored'],
	['adjustment', 'Account credit adjustment'],
	['erp_sync', 'Account credit'],
]);

/** The label for one "Payments & credits" line. */
export function statementPaymentLineLabel(line: {
	kind: StatementActivityPaymentKind;
	accountCreditType: string | null;
}): string {
	if (line.kind === 'ACCOUNT_CREDIT' && line.accountCreditType != null) {
		return (
			ACCOUNT_CREDIT_TYPE_LABELS.get(line.accountCreditType) ??
			STATEMENT_PAYMENT_KIND_LABELS.ACCOUNT_CREDIT
		);
	}
	return STATEMENT_PAYMENT_KIND_LABELS[line.kind];
}

/** PD-16379 — labels for the rows in a statement's "Adjustments" section. */
export const STATEMENT_ADJUSTMENT_KIND_LABELS: Record<
	StatementActivityAdjustmentKind,
	string
> = {
	SINCE_LAST_STATEMENT: 'Balance change since last statement',
	IN_PERIOD: 'Balance adjustment',
};

/**
 * PD-16379 — the label for the summary's opening row, naming the statement
 * it was carried from when there is one (e.g. "Opening balance (from
 * statement ST-91)").
 */
export function statementOpeningBalanceLabel(
	activity: Pick<StatementActivityModel, 'openingFrom'>,
): string {
	return activity.openingFrom != null
		? `Opening balance (from statement ${activity.openingFrom.number})`
		: 'Opening balance';
}

/** Customer-facing number of a stored statement (derived from its id). */
export function statementRecordNumber(statementId: number): string {
	return `ST-${statementId}`;
}
